import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Stat, Section, Loading, Empty, useFetch } from "./_shared";

export default function Storage() {
  const { data, loading, reload } = useFetch("/admin/storage/breakdown");
  const [busy, setBusy] = useState(false);
  if (loading) return <Loading />;
  const disks = data?.disks || []; const mongo = data?.mongo || {}; const top = data?.top_collections || [];
  const candidates = data?.cleanup_candidates || []; const suggestions = data?.suggestions || [];

  const cleanup = async (target, dry) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/storage/cleanup", { targets: target ? [target] : [], dry_run: dry });
      toast.success(dry ? `Trockenlauf: ${r.total_candidates ?? r.candidates ?? r.deleted ?? 0} Kandidaten` : "Bereinigt.");
      await reload();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Speicher" subtitle="Disk, MongoDB & Cleanup." />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {disks.map((d) => (
          <Stat key={d.mount} label={`Disk ${d.mount}`} value={`${d.used_pct}%`} hint={`${d.used_human}/${d.total_human}`} tone={d.used_pct > 85 ? "bad" : d.used_pct > 70 ? "warn" : "good"} />
        ))}
        <Stat label="MongoDB" value={mongo.storageSize_human || "—"} hint={`${mongo.collections || 0} Collections`} />
      </div>

      <Section title="Grösste Collections" testId="admin-storage-top">
        {top.length === 0 ? <Empty text="—" /> : (
          <div className="space-y-1.5">
            {top.slice(0, 15).map((c, i) => (
              <div key={c.name || i} className="flex items-center justify-between gap-2 text-sm"><span className="truncate text-foreground">{c.name}</span><span className="shrink-0 text-xs text-muted-foreground">{c.storageSize_human} · {(c.count || 0).toLocaleString("de-DE")} docs</span></div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Cleanup-Kandidaten" testId="admin-storage-cleanup"
        action={<Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => cleanup(null, true)} data-testid="admin-storage-dryrun">Trockenlauf (alle)</Button>}>
        {candidates.length === 0 ? <Empty text="Nichts zu bereinigen." /> : (
          <div className="space-y-2">
            {candidates.map((c) => (
              <div key={c.target} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-foreground">{c.collection} <span className="text-xs text-muted-foreground">({c.age_days}T)</span></p><p className="truncate text-xs text-muted-foreground">{c.description} · {c.candidates} Kandidaten</p></div>
                <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={busy || c.candidates <= 0} onClick={() => cleanup(c.target, false)} data-testid={`admin-storage-clean-${c.target}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {suggestions.length > 0 && (
        <Section title="Empfehlungen">
          <ul className="space-y-1 text-sm text-muted-foreground">{suggestions.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </Section>
      )}
    </div>
  );
}
